/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/**
 * This is the entry point for Feature Hub App integration
 */

import React, { StrictMode } from 'react';
import type { FeatureAppDefinition, FeatureAppEnvironment } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';

import { FeatureApp } from './FeatureApp';
import { getDesignLibrary } from '../alpha-helper';
import { createTrackingService } from './tracking/trackingService';

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      '@feature-hub/react': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '*', unifiedWebAlphaCommonVersion: '*' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: (env: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const { featureServices } = env;
    const serviceTracking = createTrackingService(env.featureAppId);

    if (serviceTracking) {
      serviceTracking.featureAppName = 'fa-pencil-banner';
    }

    const serviceContent = featureServices['audi-content-service'];

    const serviceSerializedStateManager = featureServices['s2:serialized-state-manager'];
    const serviceAsyncSsrManager = featureServices['s2:async-ssr-manager'];

    const audiRenderModeService = featureServices['audi-render-mode-service'];

    if (serviceAsyncSsrManager) {
      const waitForThese = async () => {
        const content = (await serviceContent?.getContent()) as Record<string, unknown>;
        serviceSerializedStateManager?.register(() => JSON.stringify(content));
      };
      const promiseWaitForThese = waitForThese();
      serviceAsyncSsrManager.scheduleRerender(promiseWaitForThese);
    }

    return {
      render: () => {
        return (
          <StrictMode>
            <UniversalEditorProvider
              contentService={serviceContent}
              renderModeService={audiRenderModeService}
            >
              <FeatureApp serviceTracking={serviceTracking} serviceContent={serviceContent} />
            </UniversalEditorProvider>
          </StrictMode>
        );
      },
    };
  },
};

export default featureAppDefinition;
